import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    message, DatePicker, Tooltip, Button, Tabs,
} from 'antd';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { HocOptions } from '../common/generic-hoc.types';
import { getPrintHistory, getBulkLabelRequestPx } from '../../network/print-history';
import { getDownloadRequests } from '../../network/common.api';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { Master, ExcelColumn } from '../../types/master-data-types';
import Paginator from '../common/paginator';
import { PrintBuckets } from './consignments.constants';
import { exportCSVFile } from './print-history.helper';
import GenericHoc from '../common/generic-hoc';
import { useTranslation } from 'react-i18next';
import { NextOrPrev, Pagination } from '../../library/Types';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
/* eslint-disable max-len */
const styles = (theme: ThemeType) => ({
    main: {
        width: '100%',
        padding: '5px 6px 6px 3px',
        height: '100%',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
    },
    filters: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        background: '#ffffff',
        marginBottom: 4,
    },
    extraFilters: {
        borderRadius: '0 2px 0 0',
        backgroundColor: '#FFFFFF',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    cellValue: {
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
    },
    cellNa: {
        color: '#999999',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
    },
    dateRange: {
        '& .ant-picker-cell-in-view::after': {
            display: 'none !important',
            content: 'none !important',
        },
        '& .ant-picker-cell:hover .ant-picker-cell-in-view:hover::after': {
            display: 'none !important',
            content: 'none !important',
        },
        '& .ant-picker-panel-container': {
            backgroundColor: '#f7f7f7 !important',
            borderRadius: '4px !important',
            display: 'flex !important',
            flexDirection: 'row-reverse !important',
        },
        '& .ant-picker-cell-in-view .ant-picker-cell-inner': {
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            width: '32px !important',
            height: '32px !important',
            padding: '0 !important',
            backgroundColor: 'white !important',
            borderRadius: '4px !important',
            margin: '1px !important',
            boxSizing: 'border-box !important',
            '&:hover': {
                backgroundColor: '#b9e0ff !important',
            },
        },
        '& .ant-picker-cell-in-view': {
            padding: '0 !important',
            '&:hover': {
                backgroundColor: 'transparent !important',
            },
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner': {
            backgroundColor: `${theme.tertiaryFillColor} !important`,
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner': {
            backgroundColor: `${theme.secondaryFillColor} !important`,
            borderColor: `${theme.secondaryFillColor} !important`,
            fontWeight: 'bold !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-disabled::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-date-panel .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-start .ant-picker-cell-inner::after': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single).ant-picker-cell-range-hover-start::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-date-panel .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-end .ant-picker-cell-inner::after': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single).ant-picker-cell-range-hover-end::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-footer': {
            minWidth: 140,
            padding: 4,
        },
        '& .ant-picker-ranges .ant-picker-preset > .ant-tag-blue': {
            color: 'black',
            background: 'transparent !important',
            borderColor: 'transparent !important',
            fontWeight: 600,
            fontSize: '12px',
            padding: '8px',
        },
        '& .ant-picker-ranges > li:hover': {
            display: 'inline-block',
            backgroundColor: `${theme.tertiaryFillColor} !important`,
            borderRadius: '4px',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner': {
        },
    },
    dateFilter: {
        width: '100%',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        '& .ant-picker': {
            borderRadius: '6px',
            width: 215,
            height: 32,
        },
        '& .ant-picker-input > input': {
            fontSize: '13px',
        },
    },
    footer: {
        height: 32,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F7F7F7',
    },
    rightFilters: {
        display: 'flex',
        alignItems: 'center',
        background: '#ffffff',
        '& .ant-btn': {
            boxSizing: 'border-box',
            height: 28,
            borderRadius: 3,
            fontFamily: 'Open Sans',
            fontSize: 12,
            fontWeight: 600,
            laterSpacing: 0,
        },
        '& .ant-btn-ghost': {
            border: `1px solid ${theme.primaryColor} `,
        },
        '& .ant-btn-primary, .ant-btn-primary:hover, .ant-btn-primary:focus': {
            backgroundColor: theme.primaryColor,
        },
        '& .ant-dropdown-menu-submenu-title': {
            padding: '12px 4px',
        },
        '& .ant-select:not(.ant-select-customize-input) .ant-select-selector, .ant-input': {
            height: 28,
        },
        '& .ant-select-selector': {
            borderRadius: '4px 0 0 4px !important',
        },
        '& .ant-input': {
            borderLeft: 'none !important',
            borderRadius: '0 4px 4px 0 !important',
        },
    },
    filter: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 16,
    },
    filterText: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        marginRight: 8,
    },
    tabsContainer: {
        '& .ant-tabs-nav-list': {
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #f0f0f0',
            minHeight: 42,
        },
        // eslint-disable-next-line max-len
        '& .ant-tabs-top > .ant-tabs-nav, .ant-tabs-bottom > .ant-tabs-nav, .ant-tabs-top > div > .ant-tabs-nav, .ant-tabs-bottom > div > .ant-tabs-nav': {
            margin: 0,
            border: 'none',
        },
        '& .ant-tabs-tab': {
            color: '#909090',
            fontWeight: 600,
            fontFamily: 'Open Sans',
            fontSize: 14,
            letterSpacing: 0,
            padding: '2px 18px !important',
            borderRadius: '4px 4px 0 0',
            border: 'none',
            backgroundColor: '#FFFFFF !important',
            cursor: 'pointer',
        },
        '& .ant-tabs-tab + .ant-tabs-tab': {
            margin: 0,
        },
        '& .ant-tabs > .ant-tabs-nav .ant-tabs-nav-operations': {
            display: 'none !important',
        },
    },
    bucketRow: {
        '& .ant-tabs-nav-operations': {
            width: 'auto',
            fontSize: 18,
            backgroundColor: '#ffffff',
        },
        '& .ant-tabs-nav-operations::after': {
            display: 'none !important',
            content: 'none !important',
        },
    },
});

interface PrintHistoryProps extends StylesProps<ReturnType<typeof styles>> {
    uiTheme: ThemeType;
    master: Master;
}

const PrintHistory = (props: PrintHistoryProps) => {
    const { classes, uiTheme, master } = props;
    const softdataCreationUnifiedFlow = master?.parts_to_show?.softdata_creation_unified_flow_enabled;

    const [prints, setPrints] = React.useState<any>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const { t } = useTranslation();
    const [dates, setDates] = React.useState({
        startDate: moment().subtract(7, 'days'),
        endDate: moment(),
    });
    const [toggle, setToggle] = React.useState<boolean>(true);
    const [bucketSelected, setBucketSelected] = React.useState<string>('all');
    const [pagination, setPagination] = React.useState<Pagination>({
        nextOrPrev: 'first',
        currentPageNumber: 1,
        resultPerPage: 15,
        isNextPresent: false,
        lastUpdatedAt: null,
    });

    const loadData = async (bucket: string) => {
        setLoading(true);
        if (bucket === 'all') {
            const response = await getPrintHistory({
                startDate: dates?.startDate?.format('YYYY-MM-DD'),
                endDate: dates?.endDate?.format('YYYY-MM-DD'),
                currentPage: pagination.currentPageNumber,
                nextOrPrev: pagination.nextOrPrev,
                resultPerPage: pagination.resultPerPage,
                lastUpdatedAt: pagination.lastUpdatedAt,
            });
            if (response?.isSuccess) {
                setPrints(response?.data?.data || []);
                setPagination({
                    ...pagination,
                    currentPageNumber: response?.data?.currentPageNo,
                    isNextPresent: response?.data?.nextPageExist,
                });
            } else {
                message.error(response.errorMessage);
            }
        }
        if (bucket === 'invoice') {
            const response = await getDownloadRequests({
                fromDate: dates?.startDate?.format('YYYY-MM-DD'),
                toDate: dates?.endDate?.format('YYYY-MM-DD'),
                dumpType: ['CUSTOMER_PORTAL_INVOICES'],
            });
            if (response.isSuccess) {
                setPrints(response?.data?.data || []);
            } else {
                message.error(response.errorMessage);
            }
        }
        if (softdataCreationUnifiedFlow && bucket === 'label') {
            let newPageNumber = pagination.currentPageNumber;
            if (pagination.nextOrPrev === 'next') {
                newPageNumber = pagination.currentPageNumber + 1;
            } else if (pagination.nextOrPrev === 'prev' && pagination.currentPageNumber > 1) {
                newPageNumber = pagination.currentPageNumber - 1;
            } else if (pagination.nextOrPrev === 'first') {
                newPageNumber = 1;
            }

            const response = await getBulkLabelRequestPx({
                fromDate: dates?.startDate?.format('YYYY-MM-DD'),
                toDate: dates?.endDate?.format('YYYY-MM-DD'),
                pageNumber: newPageNumber,
            });
            if (response.isSuccess) {
                setPrints(response?.data?.page_data || []);
                setPagination({
                    ...pagination,
                    currentPageNumber: newPageNumber,
                    isNextPresent: response?.data?.is_next_present || false,
                });
            } else {
                message.error(response.errorMessage);
            }
        }
        setLoading(false);
    };

    React.useEffect(() => {
        loadData(bucketSelected);
    }, [dates, toggle]);

    const renderRefresh = () => {
        return (
            <Tooltip title={t('reload')}>
                <Button
                    size="small"
                    type="ghost"
                    style={{
                        color: uiTheme.primaryColor,
                        fontSize: 14,
                        marginRight: 12,
                    }}
                    onClick={() => setToggle(!toggle)}
                >
                    <ReloadOutlined />
                </Button>
            </Tooltip>
        );
    };

    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        let newUpdatedAt = null;
        if (nextOrPrev === 'first') {
            newPageNumber = 1;
            newUpdatedAt = null;
        } else if (nextOrPrev === 'next') {
            const lastPrint = prints[prints.length - 1];
            newUpdatedAt = lastPrint?.createdAt;
        } else {
            const lastPrint = prints[0];
            newUpdatedAt = lastPrint?.createdAt;
        }
        const newPagination = {
            ...pagination,
            nextOrPrev,
            lastUpdatedAt: newUpdatedAt,
            currentPageNumber: newPageNumber,
        };
        setPagination(newPagination);
        setToggle(!toggle);
    };

    const getDisabled = (current: Moment) => {
        return current && moment(current) >= moment();
    };

    const renderRangePicker = () => {
        return (
            <div className={classes.filter}>
                <DatePicker.RangePicker
                    value={[dates.startDate, dates.endDate]}
                    disabledDate={(current) => getDisabled(current)}
                    placeholder={[t('From_date'), t('To_date')]}
                    allowClear={false}
                    dropdownClassName={classes.dateRange}
                    onChange={(values) => {
                        setDates({
                            ...dates,
                            startDate: moment(values?.[0]),
                            endDate: moment(values?.[1]),
                        });
                        setPagination({
                            ...pagination,
                            nextOrPrev: 'first',
                            lastUpdatedAt: null,
                        });
                        setToggle(!toggle);
                    }}
                />
            </div>
        );
    };

    const handleBucketChange = (bucket: string) => {
        if (bucket === bucketSelected) {
            return;
        }
        if (!PrintBuckets[bucket]) {
            return;
        }
        setBucketSelected(bucket);
        loadData(bucket);
    };

    const renderBucket = (bucket: string, bucketData: { name: string, width: number }) => {
        return (
            <Tabs.TabPane
                tab={t(bucketData.name)}
                key={bucket}
            />
        );
    };

    const renderBuckets = () => {
        const allowedBuckets: any = PrintBuckets;
        return (
            <div className={classes.tabsContainer}>
                <Tabs
                    className={classes.bucketRow}
                    activeKey={bucketSelected}
                    onChange={(bucket) => handleBucketChange(bucket)}
                >
                    {Object.keys(allowedBuckets).map((bucket) => renderBucket(bucket, allowedBuckets[bucket]))}
                </Tabs>
            </div>
        );
    };

    const renderDateFilter = () => {
        return (
            <div className={classes.dateFilter}>
                {renderRangePicker()}
            </div>
        );
    };

    const renderPaginator = () => {
        return (
            <div style={{ direction: 'ltr' }}>
                <Paginator
                    currentPageNumber={pagination.currentPageNumber}
                    isNextPresent={pagination.isNextPresent}
                    onNextClick={() => handlePagination('next')}
                    onPrevClick={() => handlePagination('prev')}
                    onFirstClick={() => handlePagination('first')}
                />
            </div>
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {renderRefresh()}
                {renderDateFilter()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {renderBuckets()}
                    {renderRightFilters()}
                </div>
            </div>
        );
    };


    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const renderDownload = (text: any) => {
        if (Array.isArray(text) && text.length) {
            return (
                <a
                    className={classes.row}
                    href={text[0]}
                >
                    <DownloadOutlined />
                </a>
            );
        }
        return null;
    };

    const renderErrors = (text: any, row: any) => {
        if (text?.length) {
            return (
                <div
                    className={classes.row}
                >
                    <DownloadOutlined
                        onClick={() => exportCSVFile(row.failureArray, `Errors_${row.csvId}`)}
                    />
                </div>
            );
        }
        return null;
    };

    const renderColumn = (text: any, column: ExcelColumn, row: any) => {
        if (column.column_id === 'urlArray') {
            return renderDownload(text);
        }
        if (column.column_id === 'url') {
            return renderDownload([text]);
        }
        if (column.column_id === 'downloadErrors' && bucketSelected !== 'invoice') {
            return renderErrors(row.failureArray, row);
        }
        if (column.column_id === 'failureArray') {
            return renderText(text?.length);
        }
        if (column.column_id === 'createdAt') {
            const date = moment(text).format('DD/MM/YYYY, hh:mm:ss A');
            return renderText(date);
        }
        if (column.column_id === 'type') {
            switch (text) {
                case 'THIRD_PARTY_CONSIGNMENT_LABEL':
                    return renderText('Bulk Label');
                case 'THIRD_PARTY_CONSIGNMENT_INVOICE':
                    return renderText('Bulk Invoice');
                default:
                    return renderText(text);
            }
        }
        if (text === null) {
            return <div className={classes.cellNa}>—NA—</div>;
        }
        return renderText(text);
    };

    const getColumns = (columnsList = []): any[] => {
        const columns: any = columnsList.map((column: ExcelColumn) => {
            return {
                key: column.column_id,
                title: column.column_id === 'type' ? t('details') : t(column.column_id),
                dataIndex: column.column_id,
                ellipsis: true,
                bordered: false,
                width: column.width,
                render: (text: string, row: any) => renderColumn(text, column, row),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                loading={loading}
                pagination={false}
                rowKey={(row: any) => row.id}
                columns={getColumns(PrintBuckets[bucketSelected].columns)}
                dataSource={prints}
                rowSelection={{}}
                scroll={{
                    y: 'calc(100% - 95px)',
                }}
                style={{
                    height: 'calc(100% - 80px)',
                    background: '#ffffff',
                }}
            />
        );
    };

    return (
        <div className={classes.main}>
            {renderFilters()}
            <>
                {renderTable()}
                {renderFooter()}
            </>
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        master: state.master,
        uiTheme: state.uiTheme,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(PrintHistory);
